import { FC, MouseEventHandler, useRef, useState } from "react";
import Section from "components/gonzo/parts/Section";
import AnchorTarget from "components/official/AnchorTarget";
import styled from "styled-components";
import Headline from "components/gonzo/parts/Headline";
import { CSSTransition } from "react-transition-group";
import Content from "components/gonzo/parts/Content";

const Illustration: FC = () => {
  return (
    <Section>
      <AnchorTarget id="illustration" />
      <Headline
        src={["/image/lp/gonzo/illustration/illust_title_1.png", "/image/lp/gonzo/illustration/illust_title_2.png"]}
        alt="お祝いイラスト"
      />
      <Content>
        <IllustrationContainer>
          {ILLUSTRATIONS.map((illustration, index) => (
            <IllustrationItem illustration={illustration} key={index} />
          ))}
        </IllustrationContainer>
      </Content>
    </Section>
  );
};
export default Illustration;

const IllustrationItem: FC<{ illustration: IllustrationType }> = ({ illustration }) => {
  const popupRef = useRef<HTMLDivElement>(null);
  const [opened, setOpened] = useState(false);
  const open = () => {
    const image = popupRef.current?.querySelector("img") as HTMLImageElement;
    image?.setAttribute("src", image?.getAttribute("data-src") ?? "");
    setOpened(true);
  };
  const close = () => setOpened(false);
  const stopPropagation: MouseEventHandler<HTMLImageElement> = (e) => e.stopPropagation();
  return (
    <IllustrationBox>
      <IllustrationImage src={illustration.thumb} onClick={open} />
      <IllustrationText>{illustration.text}</IllustrationText>
      <CSSTransition nodeRef={popupRef} in={opened} timeout={160} classNames="fade">
        <PopupContainer ref={popupRef} onClick={close}>
          <Popup data-src={illustration.popup} onClick={stopPropagation} />
        </PopupContainer>
      </CSSTransition>
    </IllustrationBox>
  );
};

interface IllustrationType {
  thumb: string;
  text: string;
  popup: string;
}

const ILLUSTRATIONS: IllustrationType[] = [
  {
    thumb: "/image/lp/gonzo/illustration/image/illust_01_thumb.png",
    text: "鈴森(sprite) 様",
    popup: "/image/lp/gonzo/illustration/image/illust_01.jpg",
  },
  {
    thumb: "/image/lp/gonzo/illustration/image/illust_02_thumb.png",
    text: "中野圭哉 様",
    popup: "/image/lp/gonzo/illustration/image/illust_02.jpg",
  },
  {
    thumb: "/image/lp/gonzo/illustration/image/illust_03_thumb.png",
    text: "木﨑文智 様",
    popup: "/image/lp/gonzo/illustration/image/illust_03.jpg",
  },
  {
    thumb: "/image/lp/gonzo/illustration/image/illust_04_thumb.png",
    text: "奥田佳子 様",
    popup: "/image/lp/gonzo/illustration/image/illust_04.jpg",
  },
  {
    thumb: "/image/lp/gonzo/illustration/image/illust_05_thumb.png",
    text: "佐藤陽子 様",
    popup: "/image/lp/gonzo/illustration/image/illust_05.jpg",
  },
  {
    thumb: "/image/lp/gonzo/illustration/image/message_14_thumb.png",
    text: "金春智子 様",
    popup: "/image/lp/gonzo/illustration/image/message_14.jpg",
  },
  {
    thumb: "/image/lp/gonzo/illustration/image/illust_06_thumb.png",
    text: "渡辺はじめ 様",
    popup: "/image/lp/gonzo/illustration/image/illust_06.jpg",
  },
  {
    thumb: "/image/lp/gonzo/illustration/image/illust_13_thumb.png",
    text: "追崎史敏 様",
    popup: "/image/lp/gonzo/illustration/image/illust_13.jpg",
  },
  {
    thumb: "/image/lp/gonzo/illustration/image/message_07_thumb.png",
    text: "神山修一 様",
    popup: "/image/lp/gonzo/illustration/image/message_07.jpg",
  },
  {
    thumb: "/image/lp/gonzo/illustration/image/illust_08_thumb.png",
    text: "島田フミカネ 様",
    popup: "/image/lp/gonzo/illustration/image/illust_08.jpg",
  },
  {
    thumb: "/image/lp/gonzo/illustration/image/illust_09_thumb.png",
    text: "高村和宏 様",
    popup: "/image/lp/gonzo/illustration/image/illust_09.jpg",
  },
  {
    thumb: "/image/lp/gonzo/illustration/image/message_10_thumb.png",
    text: "大畑晃一 様",
    popup: "/image/lp/gonzo/illustration/image/message_10.jpg",
  },
  {
    thumb: "/image/lp/gonzo/illustration/image/illust_12_thumb.png",
    text: "鈴木勤 様",
    popup: "/image/lp/gonzo/illustration/image/illust_12.jpg",
  },
  {
    thumb: "/image/lp/gonzo/illustration/image/illust_11_thumb.png",
    text: "森田宏幸 様",
    popup: "/image/lp/gonzo/illustration/image/illust_11.jpg",
  },
  {
    thumb: "/image/lp/gonzo/illustration/image/illust_17_thumb.png",
    text: "Shuzilow. HA 様",
    popup: "/image/lp/gonzo/illustration/image/illust_17.jpg",
  },
  {
    thumb: "/image/lp/gonzo/illustration/image/illust_16_thumb.png",
    text: "堀内修 様",
    popup: "/image/lp/gonzo/illustration/image/illust_16.jpg",
  },
  {
    thumb: "/image/lp/gonzo/illustration/image/illust_15_thumb.png",
    text: "飯島弘也 様",
    popup: "/image/lp/gonzo/illustration/image/illust_15.jpg",
  },
  {
    thumb: "/image/lp/gonzo/illustration/image/illust_18_thumb.png",
    text: "橋本英樹 様",
    popup: "/image/lp/gonzo/illustration/image/illust_18.jpg",
  },
  {
    thumb: "/image/lp/gonzo/illustration/image/illust_19_thumb.png",
    text: "白亜右月 様",
    popup: "/image/lp/gonzo/illustration/image/illust_19.jpg",
  },
];

const IllustrationContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
`;
const IllustrationBox = styled.div`
  @media screen and (max-width: 960px) {
    flex-basis: 42%;
    margin: 2%;
  }
  @media screen and (min-width: 961px) {
    flex-basis: 24%;
    margin: 1% 0.5%;
  }
`;
const IllustrationText = styled.div`
  padding: 10px 0;
  text-align: center;
  color: white;
`;
const IllustrationImage = styled.img`
  width: 100%;
`;

const PopupContainer = styled.div`
  background-color: rgba(10, 10, 10, 0.8);
  position: fixed;
  left: 0;
  width: 100%;
  z-index: 3;
  @media screen and (max-width: 960px) {
    height: calc(100vh - 50px);
    top: 50px;
  }
  @media screen and (min-width: 961px) {
    height: calc(100vh - 70px);
    top: 70px;
  }
  transition: opacity 0.2s;
  opacity: 0;
  visibility: hidden;

  &.fade-enter-active {
    opacity: 1;
    visibility: visible;
  }

  &.fade-enter-done {
    opacity: 1;
    visibility: visible;
  }

  &.fade-exit-active {
    opacity: 0;
    visibility: visible;
  }

  &.fade-exit-done {
    opacity: 0;
    visibility: hidden;
  }
`;

const Popup = styled.img`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translateX(-50%) translateY(-50%);
  @media screen and (max-width: 960px) {
    width: 90%;
  }
  @media screen and (min-width: 961px) {
    width: 600px;
  }
`;
